/**
 * Admin Moderation Feature - View
 * 
 * Elm-ish architecture: Declarative React component rendering based on model + dispatch.
 */

import React from 'react';
import { AdminModerationModel } from './model';
import { Msg } from './messages';

interface Props {
  model: AdminModerationModel;
  dispatch: (msg: Msg) => void;
}

/**
 * Admin moderation view component.
 * Renders the review queue and selected item details.
 */
export function AdminModerationView({ model, dispatch }: Props): JSX.Element {
  return (
    <div className="admin-moderation" data-testid="admin-moderation">
      <h1>Admin Moderation</h1>
      
      {model.loading && !model.selectedItem && (
        <div className="moderation-loading" data-testid="moderation-loading">
          <p>Loading review queue...</p>
        </div>
      )}

      {model.error && !model.selectedItem && (
        <div className="moderation-error" data-testid="moderation-error">
          <p>Error: {model.error}</p>
          <button onClick={() => dispatch({ type: 'load-more', page: 1 })}>
            Retry
          </button>
        </div>
      )}

      {!model.loading && !model.error && model.queue.length === 0 && !model.selectedItem && (
        <div className="moderation-empty" data-testid="moderation-empty">
          <p>No items awaiting moderation.</p>
        </div>
      )}

      {!model.selectedItem && model.queue.length > 0 && (
        <div className="moderation-queue" data-testid="moderation-queue">
          <h2>Review Queue ({model.queue.length})</h2>
          <ul>
            {model.queue.map(item => (
              <li key={item.id} data-testid={`queue-item-${item.id}`}>
                <button
                  onClick={() => dispatch({ type: 'item-selected', item })}
                  className="queue-item-button"
                >
                  <span className="item-type">{item.itemType}</span>
                  <span className="item-preview">{item.preview}</span>
                  {item.reason && <span className="item-reason">{item.reason}</span>}
                </button>
              </li>
            ))}
          </ul>
          
          {model.hasMore && (
            <button
              onClick={() => dispatch({ type: 'load-more' })}
              disabled={model.loading}
              data-testid="load-more-button"
            >
              {model.loading ? 'Loading...' : 'Load More'}
            </button>
          )}
        </div>
      )}

      {model.selectedItem && (
        <div className="moderation-item-detail" data-testid="moderation-item-detail">
          <h2>Item Review</h2>
          
          <div className="item-info">
            <p><strong>ID:</strong> {model.selectedItem.id}</p>
            <p><strong>Type:</strong> {model.selectedItem.itemType}</p>
            <p><strong>Preview:</strong> {model.selectedItem.preview}</p>
            {model.selectedItem.reason && (
              <p><strong>Reason:</strong> {model.selectedItem.reason}</p>
            )}
            <p><strong>Created:</strong> {new Date(model.selectedItem.createdAt).toLocaleString()}</p>
          </div>

          {model.pendingAction && (
            <div className="action-confirm" data-testid="action-confirm">
              <p>Confirm {model.pendingAction} action?</p>
              <div className="action-buttons">
                <button
                  onClick={() => dispatch({ type: 'action-confirmed' })}
                  className="confirm-button"
                >
                  Confirm
                </button>
                <button
                  onClick={() => dispatch({ type: 'action-cancelled' })}
                  className="cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!model.pendingAction && (
            <div className="item-actions" data-testid="item-actions">
              <button
                onClick={() => dispatch({ type: 'approve-intent', item: model.selectedItem })}
                className="approve-button"
              >
                Approve
              </button>
              <button
                onClick={() => dispatch({ type: 'reject-intent', item: model.selectedItem })}
                className="reject-button"
              >
                Reject
              </button>
              <button
                onClick={() => dispatch({ type: 'item-deselected' })}
                className="back-button"
              >
                Back to Queue
              </button>
            </div>
          )}
        </div>
      )}

      <style>{`
        .admin-moderation {
          max-width: 800px;
          margin: 0 auto;
        }
        .admin-moderation h1 {
          font-size: 1.5rem;
          margin-bottom: 1rem;
        }
        .admin-moderation h2 {
          font-size: 1.2rem;
          margin-bottom: 0.75rem;
        }
        .moderation-loading,
        .moderation-empty,
        .moderation-error {
          text-align: center;
          padding: 2rem;
          color: var(--color-text-muted);
        }
        .moderation-error {
          color: var(--color-error);
        }
        .moderation-queue ul {
          list-style: none;
          padding: 0;
        }
        .moderation-queue li {
          margin-bottom: 0.5rem;
        }
        .queue-item-button {
          width: 100%;
          text-align: left;
          padding: 0.75rem;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          cursor: pointer;
        }
        .queue-item-button:hover {
          border-color: var(--color-accent);
        }
        .item-type {
          display: inline-block;
          padding: 0.125rem 0.5rem;
          background: var(--color-accent);
          color: white;
          border-radius: var(--radius-sm);
          font-size: 0.75rem;
          margin-right: 0.5rem;
        }
        .item-preview {
          display: block;
          margin-top: 0.25rem;
        }
        .item-reason {
          display: block;
          font-size: 0.85rem;
          color: var(--color-text-muted);
          margin-top: 0.25rem;
        }
        .moderation-item-detail {
          background: var(--color-surface);
          padding: 1rem;
          border-radius: var(--radius);
          border: 1px solid var(--color-border);
        }
        .item-info p {
          margin: 0.5rem 0;
        }
        .item-actions {
          display: flex;
          gap: 0.5rem;
          margin-top: 1rem;
        }
        .approve-button {
          background: var(--color-success);
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: var(--radius);
          cursor: pointer;
        }
        .reject-button {
          background: var(--color-error);
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: var(--radius);
          cursor: pointer;
        }
        .back-button {
          background: transparent;
          border: 1px solid var(--color-border);
          padding: 0.5rem 1rem;
          border-radius: var(--radius);
          cursor: pointer;
          margin-left: auto;
        }
        .action-confirm {
          margin-top: 1rem;
          padding: 1rem;
          background: var(--color-warning-bg, #fff8e0);
          border-radius: var(--radius);
        }
        .action-buttons {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }
        .confirm-button {
          background: var(--color-accent);
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: var(--radius);
          cursor: pointer;
        }
        .cancel-button {
          background: transparent;
          border: 1px solid var(--color-border);
          padding: 0.5rem 1rem;
          border-radius: var(--radius);
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}